"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DropdownSelect from "@/components/ui/DropdownSelect";
import DrawerPanel from "@/components/ui/DrawerPanel";
import FileInput from "@/components/form/input/FileInput";
import Input from "@/components/form/input/InputField";
import Radio from "@/components/form/input/Radio";
import Checkbox from "@/components/form/input/Checkbox";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { handleApiError } from "@/lib/api";
import {
  DOC_SLOTS,
  docCount,
  getDocUrl,
  hasUploadedDoc,
  resolveAssetHref,
} from "@/lib/driverKyc";
import DriverKycPrint from "@/components/print/DriverKycPrint";
import { EyeIcon, PencilIcon, TrashBinIcon } from "@/icons";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const ACCOUNT_TYPES = ["SAVINGS", "CURRENT", "SALARY"];

const ACCOUNT_TYPE_OPTIONS = ACCOUNT_TYPES.map((t) => ({ value: t, label: t }));

function emptyBankRow() {
  return {
    account_no: "",
    ifsc_code: "",
    bank_name: "",
    branch: "",
    account_type: "SAVINGS",
  };
}

function emptyForm() {
  return {
    name: "",
    employee_id: "",
    address: "",
    contact_no: "",
    is_active: true,
    bank_accounts: [emptyBankRow()],
    doc_pan: null,
    doc_aadhar_front: null,
    doc_aadhar_back: null,
    doc_licence_front: null,
    doc_licence_back: null,
    doc_pf_certificate: null,
    pendingRemove: {},
  };
}

function mapDriverToForm(d) {
  const raw = Array.isArray(d?.bank_accounts) ? d.bank_accounts : [];
  const bank_accounts =
    raw.length > 0
      ? raw.map((b) => ({
          account_no: String(b?.account_no ?? ""),
          ifsc_code: String(b?.ifsc_code ?? ""),
          bank_name: String(b?.bank_name ?? ""),
          branch: String(b?.branch ?? ""),
          account_type: String(b?.account_type ?? "SAVINGS") || "SAVINGS",
        }))
      : [emptyBankRow()];

  return {
    name: d?.name ?? "",
    employee_id: d?.employee_id ?? "",
    address: d?.address ?? "",
    contact_no: d?.contact_no ?? d?.contact ?? "",
    is_active: d?.is_active !== false,
    bank_accounts,
    doc_pan: null,
    doc_aadhar_front: null,
    doc_aadhar_back: null,
    doc_licence_front: null,
    doc_licence_back: null,
    doc_pf_certificate: null,
    pendingRemove: {},
  };
}

export default function RmcDrivers() {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [driverDetail, setDriverDetail] = React.useState(null);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [driverToDelete, setDriverToDelete] = React.useState(null);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [viewDriver, setViewDriver] = React.useState(null);
  const [viewLoading, setViewLoading] = React.useState(false);
  const kycPrintRef = React.useRef(null);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ["drivers", { include_inactive: true }],
    queryFn: async () =>
      (await rmcClient.listDrivers({ include_inactive: true })).data,
  });

  const closeDrawer = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setDriverDetail(null);
    setLoadingDetail(false);
  };

  const openAdd = () => {
    setEditingId(null);
    setDriverDetail(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setViewDriver(null);
    setViewLoading(false);
  };

  const openView = async (row) => {
    setViewOpen(true);
    setViewDriver(null);
    setViewLoading(true);
    try {
      const { data } = await rmcClient.getDriver(row.id);
      setViewDriver(data);
    } catch (e) {
      toast.error(handleApiError(e));
      setViewDriver(row);
    } finally {
      setViewLoading(false);
    }
  };

  const openEdit = async (row) => {
    setEditingId(row.id);
    setOpen(true);
    setLoadingDetail(true);
    setForm(emptyForm());
    setDriverDetail(null);
    try {
      const { data } = await rmcClient.getDriver(row.id);
      setDriverDetail(data);
      setForm(mapDriverToForm(data));
    } catch (e) {
      toast.error(handleApiError(e));
      setDriverDetail(row);
      setForm(mapDriverToForm(row));
    } finally {
      setLoadingDetail(false);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      if (!form.name?.trim()) throw new Error("empty");
      const remove_documents = DOC_SLOTS.map((s) => s.code).filter(
        (c) => form.pendingRemove[c],
      );
      const payload = {
        name: form.name.trim(),
        employee_id: form.employee_id.trim(),
        address: form.address.trim(),
        contact_no: form.contact_no.trim(),
        is_active: form.is_active,
        bank_accounts: form.bank_accounts,
        doc_pan: form.doc_pan,
        doc_aadhar_front: form.doc_aadhar_front,
        doc_aadhar_back: form.doc_aadhar_back,
        doc_licence_front: form.doc_licence_front,
        doc_licence_back: form.doc_licence_back,
        doc_pf_certificate: form.doc_pf_certificate,
        ...(editingId != null && remove_documents.length > 0
          ? { remove_documents }
          : {}),
      };
      if (editingId != null) {
        return rmcClient.updateDriver(editingId, payload);
      }
      return rmcClient.createDriver(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success(editingId != null ? "Driver updated" : "Driver saved");
      closeDrawer();
    },
    onError: (e) => {
      if (e.message === "empty") toast.error("Name is required");
      else toast.error(handleApiError(e));
    },
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteDriver(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver deleted");
      setDriverToDelete(null);
    },
    onError: (e) => {
      setDriverToDelete(null);
      toast.error(handleApiError(e));
    },
  });

  const detailForDocs = driverDetail;

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Driver Master" />
      <div className="flex justify-end">
        <Button size="sm" onClick={openAdd}>
          Add Driver
        </Button>
      </div>
      <DataTable
        columns={[
          { key: "name", header: "Name" },
          {
            key: "employee_id",
            header: "Employee ID",
            render: (r) => r.employee_id || "—",
          },
          { key: "address", header: "Address", render: (r) => r.address || "—" },
          {
            key: "contact_no",
            header: "Contact",
            render: (r) => r.contact_no || r.contact || "—",
          },
          {
            key: "is_active",
            header: "Status",
            render: (r) => (r.is_active !== false ? "Active" : "Inactive"),
          },
          {
            key: "documents",
            header: "Documents",
            render: (r) => {
              const n = docCount(r);
              return n ? `${n} on file` : "—";
            },
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={`View details ${r.name ?? r.id}`}
                  title="View details & documents"
                  disabled={viewLoading || save.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openView(r);
                  }}
                >
                  <EyeIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Edit driver ${r.name ?? r.id}`}
                  title="Edit"
                  disabled={save.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(r);
                  }}
                >
                  <PencilIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete driver ${r.name ?? r.id}`}
                  title="Delete"
                  disabled={remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400 disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDriverToDelete({ id: r.id, name: r.name });
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />
      <DrawerPanel
        open={open}
        onClose={() => {
          if (!save.isPending) closeDrawer();
        }}
        title={editingId != null ? "Edit driver" : "Add driver"}
        widthClass="max-w-2xl"
      >
        {loadingDetail ? (
          <p className="text-sm text-gray-400">Loading driver…</p>
        ) : (
          <div className="max-h-[calc(100vh-8rem)] space-y-5 overflow-y-auto pr-1">
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Employee ID</Label>
                <Input
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. EMP-1042"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact no</Label>
                <Input
                  value={form.contact_no}
                  onChange={(e) => setForm({ ...form, contact_no: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-2 flex items-center gap-6">
                  <Radio
                    id="driver-status-active"
                    name="driver-status"
                    value="active"
                    label="Active"
                    checked={form.is_active}
                    onChange={() => setForm({ ...form, is_active: true })}
                  />
                  <Radio
                    id="driver-status-inactive"
                    name="driver-status"
                    value="inactive"
                    label="Inactive"
                    checked={!form.is_active}
                    onChange={() => setForm({ ...form, is_active: false })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t border-gray-800 pt-4">
              <p className="text-sm font-medium text-gray-200">Bank accounts</p>
              <p className="text-[11px] text-gray-500">
                One or more accounts; empty rows are ignored on save.
              </p>
              {form.bank_accounts.map((row, idx) => (
                <div
                  key={idx}
                  className="space-y-2 rounded-lg border border-gray-800 bg-gray-950/50 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-400">Account {idx + 1}</span>
                    {form.bank_accounts.length > 1 && (
                      <button
                        type="button"
                        className="text-xs text-red-400 hover:underline"
                        onClick={() =>
                          setForm({
                            ...form,
                            bank_accounts: form.bank_accounts.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label>Account no</Label>
                      <Input
                        value={row.account_no}
                        onChange={(e) => {
                          const next = [...form.bank_accounts];
                          next[idx] = { ...next[idx], account_no: e.target.value };
                          setForm({ ...form, bank_accounts: next });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>IFSC</Label>
                      <Input
                        value={row.ifsc_code}
                        onChange={(e) => {
                          const next = [...form.bank_accounts];
                          next[idx] = { ...next[idx], ifsc_code: e.target.value };
                          setForm({ ...form, bank_accounts: next });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Bank name</Label>
                      <Input
                        value={row.bank_name}
                        onChange={(e) => {
                          const next = [...form.bank_accounts];
                          next[idx] = { ...next[idx], bank_name: e.target.value };
                          setForm({ ...form, bank_accounts: next });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Branch</Label>
                      <Input
                        value={row.branch}
                        onChange={(e) => {
                          const next = [...form.bank_accounts];
                          next[idx] = { ...next[idx], branch: e.target.value };
                          setForm({ ...form, bank_accounts: next });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <DropdownSelect
                        label="Account type"
                        value={row.account_type}
                        onChange={(v) => {
                          const next = [...form.bank_accounts];
                          next[idx] = { ...next[idx], account_type: v };
                          setForm({ ...form, bank_accounts: next });
                        }}
                        options={ACCOUNT_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    bank_accounts: [...form.bank_accounts, emptyBankRow()],
                  })
                }
              >
                Add bank account
              </Button>
            </div>

            <div className="space-y-3 border-t border-gray-800 pt-4">
              <p className="text-sm font-medium text-gray-200">Documents</p>
              <p className="text-[11px] text-gray-500">
                Upload files to attach; JSON save is used when no files are selected. Multipart is
                used when any file is uploaded (or when removing existing documents on edit).
              </p>
              {DOC_SLOTS.map((slot) => (
                <div key={slot.fileKey}>
                  <Label>{slot.label}</Label>
                  <div className="mt-1" key={`${editingId ?? "new"}-${slot.fileKey}`}>
                    <FileInput
                      accept="image/*,.pdf,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        setForm((s) => ({ ...s, [slot.fileKey]: f || null }));
                      }}
                    />
                  </div>
                  {form[slot.fileKey] && (
                    <p className="mt-1 text-[11px] text-gray-400">
                      Selected: {form[slot.fileKey].name}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {editingId != null && detailForDocs && (
              <div className="space-y-2 border-t border-gray-800 pt-4">
                <p className="text-sm font-medium text-gray-200">Remove existing documents</p>
                <p className="text-[11px] text-gray-500">
                  Checked items are sent as{" "}
                  <code className="text-gray-400">remove_documents</code> on save (multipart).
                </p>
                <div className="space-y-2">
                  {DOC_SLOTS.filter((s) => hasUploadedDoc(detailForDocs, s)).map((slot) => (
                    <Checkbox
                      key={slot.code}
                      id={`rm-${slot.code}`}
                      label={`Remove ${slot.label}`}
                      checked={Boolean(form.pendingRemove[slot.code])}
                      onChange={(checked) =>
                        setForm({
                          ...form,
                          pendingRemove: { ...form.pendingRemove, [slot.code]: checked },
                        })
                      }
                    />
                  ))}
                  {DOC_SLOTS.every((s) => !hasUploadedDoc(detailForDocs, s)) && (
                    <p className="text-xs text-gray-500">No documents on file for this driver.</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-gray-800 pt-4">
              <Button
                size="sm"
                disabled={!form.name.trim() || save.isPending || loadingDetail}
                onClick={() => save.mutate()}
              >
                {save.isPending ? "Saving…" : editingId != null ? "Update" : "Save"}
              </Button>
              <Button size="sm" variant="outline" onClick={closeDrawer} disabled={save.isPending}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DrawerPanel>

      <Modal isOpen={viewOpen} onClose={closeView} className="max-w-2xl w-full">
        <div className="max-h-[min(78vh,640px)] overflow-hidden">
          <div className="border-b border-gray-800 pb-3">
            <h3 className="text-base font-semibold text-white">Driver details</h3>
            {viewDriver?.name && (
              <p className="mt-0.5 text-sm text-gray-400">{viewDriver.name}</p>
            )}
          </div>
          {viewLoading ? (
            <p className="py-8 text-center text-sm text-gray-400">Loading driver…</p>
          ) : viewDriver ? (
            <div className="max-h-[min(62vh,520px)] space-y-6 overflow-y-auto py-4 pr-1">
              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Profile
                </h4>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-gray-500">Name</dt>
                    <dd className="mt-0.5 text-sm text-gray-100">{viewDriver.name ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Employee ID</dt>
                    <dd className="mt-0.5 text-sm text-gray-100">
                      {viewDriver.employee_id ?? "—"}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-gray-500">Address</dt>
                    <dd className="mt-0.5 text-sm text-gray-100">{viewDriver.address ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Contact</dt>
                    <dd className="mt-0.5 text-sm text-gray-100">
                      {viewDriver.contact_no ?? viewDriver.contact ?? "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Status</dt>
                    <dd className="mt-0.5 text-sm text-gray-100">
                      {viewDriver.is_active !== false ? "Active" : "Inactive"}
                    </dd>
                  </div>
                  {viewDriver.id != null && (
                    <div>
                      <dt className="text-xs text-gray-500">Driver ID</dt>
                      <dd className="mt-0.5 text-sm text-gray-100">{viewDriver.id}</dd>
                    </div>
                  )}
                </dl>
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Bank accounts
                </h4>
                {Array.isArray(viewDriver.bank_accounts) && viewDriver.bank_accounts.length > 0 ? (
                  <div className="space-y-3">
                    {viewDriver.bank_accounts.map((acc, i) => (
                      <div
                        key={`${acc?.account_no ?? ""}-${i}`}
                        className="rounded-lg border border-gray-800 bg-gray-900/40 p-3 text-sm"
                      >
                        <p className="font-medium text-gray-200">
                          {acc?.bank_name || "Account"} ·{" "}
                          <span className="text-gray-400">{acc?.account_type ?? "—"}</span>
                        </p>
                        <p className="mt-1 text-gray-300">
                          <span className="text-gray-500">A/C:</span> {acc?.account_no ?? "—"}
                        </p>
                        <p className="text-gray-300">
                          <span className="text-gray-500">IFSC:</span> {acc?.ifsc_code ?? "—"}
                        </p>
                        <p className="text-gray-300">
                          <span className="text-gray-500">Branch:</span> {acc?.branch ?? "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No bank accounts on file.</p>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Documents
                </h4>
                {docCount(viewDriver) === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-700 bg-gray-900/30 px-4 py-6 text-center">
                    <p className="text-sm text-gray-400">No documents uploaded.</p>
                    <p className="mt-1 text-xs text-gray-600">
                      Upload PAN, Aadhaar, licence, or PF documents when adding or editing this driver.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <ul className="divide-y divide-gray-800 rounded-lg border border-gray-800">
                      {DOC_SLOTS.map((slot) => {
                        const rawUrl = getDocUrl(viewDriver, slot);
                        const href = rawUrl ? resolveAssetHref(rawUrl) : null;
                        return (
                          <li
                            key={slot.code}
                            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"
                          >
                            <span className="text-gray-200">{slot.label}</span>
                            {href ? (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-400 hover:underline"
                              >
                                View / download
                              </a>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-gray-500">No data.</p>
          )}
          <div className="flex flex-wrap gap-2 border-t border-gray-800 pt-4">
            <Button
              size="sm"
              disabled={!viewDriver?.id || viewLoading}
              onClick={() => kycPrintRef.current?.openPrint()}
            >
              Print KYC
            </Button>
            <Button size="sm" variant="outline" onClick={closeView}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <DriverKycPrint ref={kycPrintRef} driver={viewDriver} />

      <Modal
        isOpen={!!driverToDelete}
        onClose={() => setDriverToDelete(null)}
        className="max-w-md"
        showCloseButton={false}
      >
        <div className="space-y-4 p-1">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Delete driver <span className="font-semibold">{driverToDelete?.name}</span>?
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setDriverToDelete(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() => driverToDelete && remove.mutate(driverToDelete.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
